import { useRef, useEffect, useState } from 'react';
import { purchaseTiers, usageMethods } from '../../../mocks/homeData';

export default function PurchaseSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="purchase" className="bg-[#FAFAF8] py-24 px-6" ref={sectionRef}>
      <div className="max-w-7xl mx-auto">

        {/* 제목 */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-[#8B7355] text-xs font-medium tracking-[0.3em] uppercase mb-3">Purchase</p>
          <h2 className="text-[#2C2825] text-4xl lg:text-5xl font-light mb-4">
            구매 안내
          </h2>
          <p className="text-[#6B5F52] text-base">
            나에게 맞는 용량을 선택하세요
          </p>
        </div>

        {/* 가격 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24">
          {purchaseTiers.map((tier, i) => (
            <div
              key={tier.id}
              className={`relative rounded-2xl p-8 border transition-all duration-500 ${
                tier.highlight
                  ? 'bg-[#2C2825] border-[#2C2825] text-white'
                  : 'bg-white border-[#EDE6DB] text-[#2C2825]'
              } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {tier.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C4A882] text-white text-xs px-4 py-1 rounded-full font-medium whitespace-nowrap">
                  가장 인기
                </span>
              )}
              <p className={`text-xs tracking-[0.2em] uppercase mb-1 ${tier.highlight ? 'text-[#C4A882]' : 'text-[#8B7355]'}`}>
                {tier.subtitle}
              </p>
              <h3 className={`text-xl font-medium mb-3 ${tier.highlight ? 'text-white' : 'text-[#2C2825]'}`}>
                {tier.name}
              </h3>
              <p className={`text-sm leading-relaxed mb-6 ${tier.highlight ? 'text-white/60' : 'text-[#9B8E82]'}`}>
                {tier.description}
              </p>
              <div className="mb-6">
                <span className={`text-4xl font-light ${tier.highlight ? 'text-white' : 'text-[#2C2825]'}`}>
                  ₩{tier.price}
                </span>
                <span className={`text-sm ml-1 ${tier.highlight ? 'text-white/60' : 'text-[#9B8E82]'}`}>/ {tier.unit}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {tier.features.map((item: string) => (
                  <li key={item} className="flex items-center gap-2 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className={`ri-check-line text-sm ${tier.highlight ? 'text-[#C4A882]' : 'text-[#8B7355]'}`}></i>
                    </div>
                    <span className={tier.highlight ? 'text-white/80' : 'text-[#6B5F52]'}>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={scrollToContact}
                className={`w-full py-3 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  tier.highlight
                    ? 'bg-[#C4A882] text-white hover:bg-[#B8956E]'
                    : 'bg-[#F5F0E8] text-[#2C2825] hover:bg-[#8B7355] hover:text-white border border-[#EDE6DB]'
                }`}
              >
                {tier.cta}
              </button>
            </div>
          ))}
        </div>

        {/* 사용 방법 섹션 - 전체 카테고리 노출 */}
        <div className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center mb-14">
            <p className="text-[#8B7355] text-xs font-medium tracking-[0.3em] uppercase mb-3">How to Use</p>
            <h3 className="text-[#2C2825] text-3xl lg:text-4xl font-light mb-4">
              다양한 활용 방법
            </h3>
            <p className="text-[#6B5F52] text-sm max-w-xl mx-auto leading-relaxed">
              ZEO ZEOLITE 지오 지올라이트는 Food Detox, Beauty, Wellness 루틴 등<br />
              다양한 방식으로 활용할 수 있습니다.
            </p>
          </div>

          <div className="space-y-14">
            {usageMethods.map((method) => (
              <div key={method.category}>
                {/* 카테고리 헤더 */}
                <div className="flex items-center gap-3 mb-7">
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ backgroundColor: method.bgColor }}
                  >
                    <i className={`${method.icon} text-base`} style={{ color: method.color }}></i>
                  </div>
                  <h4
                    className="text-[#2C2825] text-xl font-medium"
                    style={{ color: method.color }}
                  >
                    {method.category}
                  </h4>
                  <div className="flex-1 h-px bg-[#EDE6DB] ml-2"></div>
                </div>

                {/* 카테고리 카드 그리드 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {method.items.map((item, idx) => (
                    <div
                      key={item.title}
                      className="bg-white rounded-2xl border border-[#EDE6DB] p-7"
                      style={{ transitionDelay: `${idx * 80}ms` }}
                    >
                      <div className="flex items-center gap-3 mb-5">
                        <div
                          className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0"
                          style={{ backgroundColor: method.bgColor }}
                        >
                          <i className={`${method.icon} text-sm`} style={{ color: method.color }}></i>
                        </div>
                        <h5 className="text-[#2C2825] text-base font-medium">{item.title}</h5>
                      </div>
                      <ol className="space-y-3">
                        {item.steps.map((step, si) => (
                          <li key={si} className="flex items-start gap-3">
                            <span
                              className="w-5 h-5 flex items-center justify-center rounded-full text-xs font-medium flex-shrink-0 mt-0.5"
                              style={{ backgroundColor: method.bgColor, color: method.color }}
                            >
                              {si + 1}
                            </span>
                            <p className="text-[#6B5F52] text-sm leading-relaxed">{step}</p>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
